import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { api } from '@/lib/api'
import type { PurchaseRequestSummary } from '@/lib/api-types'
import { flags } from '@/lib/flags'
import { formatDate, formatMoney } from '@/lib/format'
import { PURCHASE_REQUEST_STATUS_LABELS, purchaseRequestChipClasses } from '@/lib/status'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

export function PurchasingListPage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['purchase-requests'],
    queryFn: ({ signal }) => api.listPurchaseRequests({ signal }),
    staleTime: 15_000,
  })

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title="Purchasing"
        description="Purchase order requests you’ve sent for review, and what Green Line decided."
        actions={
          <Button asChild>
            <Link to="/purchasing/new">Start a purchase order request</Link>
          </Button>
        }
      />

      <div className="mt-8">
        {isPending ? (
          <div className="flex justify-center py-16">
            <Spinner label="Loading your order requests…" />
          </div>
        ) : isError ? (
          <ErrorState message="We couldn’t load your order requests." onRetry={() => void refetch()} />
        ) : data.requests.length === 0 ? (
          <EmptyState
            title="No order requests yet"
            action={
              <Button asChild>
                <Link to="/purchasing/new">Start a purchase order request</Link>
              </Button>
            }
          >
            {flags.purchasingPricing
              ? 'Skip the email back-and-forth: pick your products, see live price-break pricing, attach your PO, and send the request straight to your Green Line team for review.'
              : 'Skip the email back-and-forth: pick your products and pack sizes, attach your PO, and send the request straight to your Green Line team for review.'}
          </EmptyState>
        ) : (
          <ul className="space-y-3">
            {data.requests.map((r) => (
              <PurchaseRequestCard key={r.id} request={r} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function PurchaseRequestCard({ request }: { request: PurchaseRequestSummary }) {
  return (
    <li>
      <Card padding="sm">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <Link
            to={`/purchasing/${request.id}`}
            className="rounded-sm font-mono text-[0.8125rem] text-inkMid underline-offset-4 hover:text-brand hover:underline"
          >
            {request.name}
          </Link>
          <span className="font-mono text-[0.8125rem] text-inkMid">
            {formatMoney(request.amount, request.currency)}
          </span>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-mute">
          <StatusChip
            label={
              request.status === 'accepted' && request.stageLabel
                ? `Accepted · ${request.stageLabel}`
                : PURCHASE_REQUEST_STATUS_LABELS[request.status]
            }
            chipClasses={purchaseRequestChipClasses(request.status)}
          />
          {request.poNumber ? <span>PO {request.poNumber}</span> : null}
          <span>{request.lineCount === 1 ? '1 line' : `${request.lineCount} lines`}</span>
          {request.submittedAt ? <span>Sent {formatDate(request.submittedAt)}</span> : null}
          {request.submittedByName ? <span>by {request.submittedByName}</span> : null}
        </div>

        {request.status === 'accepted' && request.orderId ? (
          <Link
            to={`/orders/${request.orderId}`}
            className="mt-3 inline-flex items-center gap-1 rounded-sm text-sm font-semibold text-brand underline-offset-4 hover:underline"
          >
            This request became an order
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </Link>
        ) : null}

        {request.status === 'declined' && request.reviewNote ? (
          <p className="mt-3 rounded-md border border-rust/30 bg-rust/5 px-3 py-2 text-sm leading-relaxed text-inkMid">
            <span className="font-semibold text-rust">From your Green Line team:</span>{' '}
            {request.reviewNote}
          </p>
        ) : null}
      </Card>
    </li>
  )
}
